/**
 * Multi-select result filtering (STN client round 2026-08). Replaces the old
 * two-boolean MapsFilters model with three checkbox groups:
 *
 *   attributes — Has LinkedIn profile / Verified work history / US-based
 *   timezones  — derived from the recorded state (utils/timezone.ts)
 *   states     — the candidate's recorded US state (USPS codes)
 *
 * Semantics: OR within a group, AND across groups; an empty group constrains
 * nothing. THE HONEST-BLANKS POLICY HOLDS EVERYWHERE: a timezone/state
 * selection excludes only candidates whose value is POSITIVELY KNOWN and not
 * selected — unknown-location candidates always stay visible (mirrors
 * IsKnownNonUs / IsKnownOtherState and the backend's structuredFit policy).
 * The two presence attributes (LinkedIn, verified history) are the sanctioned
 * exception: they are explicit user-requested opt-ins for candidates that
 * HAVE the datum, not a silent penalty for lacking it.
 *
 * Everything here is pure and vitest-covered. Facet counts are recomputed by
 * the filter bar ONLY when its inputs change (never per CD cycle).
 */
import type { MapsCandidate } from '../models/maps.types';
import { IsKnownNonUs, NormalizeUsState, StateDisplayName } from './location';
import { TIMEZONE_LABELS, TIMEZONE_ORDER, TimezoneOf, type MapsTimezoneKey } from './timezone';

export type MapsFilterAttribute = 'hasLinkedIn' | 'verifiedHistory' | 'usBased';

export const ATTRIBUTE_LABELS: Record<MapsFilterAttribute, string> = {
    hasLinkedIn: 'Has LinkedIn profile',
    verifiedHistory: 'Verified work history',
    usBased: 'US-based',
};

export interface MapsFilterState {
    attributes: ReadonlySet<MapsFilterAttribute>;
    timezones: ReadonlySet<MapsTimezoneKey>;
    /** Lowercase USPS codes. */
    states: ReadonlySet<string>;
}

export function EmptyFilterState(): MapsFilterState {
    return { attributes: new Set(), timezones: new Set(), states: new Set() };
}

export function CountActiveFilters(f: MapsFilterState): number {
    return f.attributes.size + f.timezones.size + f.states.size;
}

/** The candidate's recorded state as a lowercase USPS code; null when absent
 *  or not one of the 50+DC. */
export function CandidateStateCode(c: MapsCandidate): string | null {
    const state = c.location?.state?.trim();
    if (!state) return null;
    const code = NormalizeUsState(state);
    return StateDisplayName(code) !== null ? code : null;
}

function matchesAttributes(c: MapsCandidate, selected: ReadonlySet<MapsFilterAttribute>): boolean {
    if (selected.size === 0) return true;
    // Attributes AND together — each checked box is its own requirement
    // ("US-based" + "Has LinkedIn" means both), unlike the value groups
    // below where selections OR (a candidate has ONE timezone).
    for (const attr of selected) {
        if (attr === 'hasLinkedIn' && !c.linkedInUrl) return false;
        if (attr === 'verifiedHistory' && (c.employmentHistory ?? []).length === 0) return false;
        if (attr === 'usBased' && IsKnownNonUs(c.location)) return false;
    }
    return true;
}

function matchesTimezones(c: MapsCandidate, selected: ReadonlySet<MapsTimezoneKey>): boolean {
    if (selected.size === 0) return true;
    const zone = TimezoneOf(c.location);
    // Unknown timezone never excludes (honest blanks).
    return zone === null || selected.has(zone);
}

function matchesStates(c: MapsCandidate, selected: ReadonlySet<string>): boolean {
    if (selected.size === 0) return true;
    const code = CandidateStateCode(c);
    // Unknown state never excludes (honest blanks).
    return code === null || selected.has(code);
}

/** AND across the three groups. Preserves input order (Best Match = server order). */
export function ApplyFilters(results: readonly MapsCandidate[], f: MapsFilterState): MapsCandidate[] {
    return results.filter(
        (c) => matchesAttributes(c, f.attributes) && matchesTimezones(c, f.timezones) && matchesStates(c, f.states)
    );
}

export interface TimezoneFacet {
    key: MapsTimezoneKey;
    label: string;
    count: number;
}

export interface StateFacet {
    /** Lowercase USPS code. */
    code: string;
    /** "California (CA)" */
    label: string;
    count: number;
}

export interface FacetCounts {
    /** Mockup order, only zones with ≥1 positively-known match. */
    timezones: TimezoneFacet[];
    /** Sorted by state name, only states with ≥1 positively-known match. */
    states: StateFacet[];
}

/**
 * Drill-down facet counts: each group is counted over the results filtered by
 * every OTHER group (standard faceting — a group's own selections must not
 * zero out its sibling rows). Counts include only positively-known values;
 * unknown-location candidates appear in no row (they can't be filtered out,
 * so a row count for them would be a lie).
 *
 * `officeStateCode` (the brief's work state, when it names one): that state's
 * row ALWAYS renders — count 0 included — so one click reproduces the old
 * in-state filter even when no candidate shares the office state (full-suite
 * run 2026-08-11, finding 1: a DC office with zero DC candidates left no row
 * to tag, silently dropping the affordance).
 */
export function ComputeFacetCounts(
    results: readonly MapsCandidate[],
    f: MapsFilterState,
    officeStateCode: string | null = null
): FacetCounts {
    const forTimezones = results.filter((c) => matchesAttributes(c, f.attributes) && matchesStates(c, f.states));
    const tzCounts = new Map<MapsTimezoneKey, number>();
    for (const c of forTimezones) {
        const zone = TimezoneOf(c.location);
        if (zone) tzCounts.set(zone, (tzCounts.get(zone) ?? 0) + 1);
    }

    const forStates = results.filter((c) => matchesAttributes(c, f.attributes) && matchesTimezones(c, f.timezones));
    const stateCounts = new Map<string, number>();
    for (const c of forStates) {
        const code = CandidateStateCode(c);
        if (code) stateCounts.set(code, (stateCounts.get(code) ?? 0) + 1);
    }
    if (officeStateCode && StateDisplayName(officeStateCode) !== null && !stateCounts.has(officeStateCode)) {
        stateCounts.set(officeStateCode, 0);
    }

    return {
        timezones: TIMEZONE_ORDER.filter((key) => (tzCounts.get(key) ?? 0) > 0).map((key) => ({
            key,
            label: TIMEZONE_LABELS[key],
            count: tzCounts.get(key)!,
        })),
        states: [...stateCounts.entries()]
            .map(([code, count]) => ({ code, label: `${StateDisplayName(code)} (${code.toUpperCase()})`, count }))
            .sort((a, b) => a.label.localeCompare(b.label)),
    };
}
